import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { VioTablesV2Component } from 'vio-tables-v2';
import type { VT2_TABLE_CONFIG } from 'vio-tables-v2';
import { ApiRequestsService } from '../../services/api-requests.service';

declare const bootstrap: any;

@Component({
  selector: 'app-document-type-master',
  standalone: true,
  imports: [CommonModule, FormsModule, VioTablesV2Component],
  templateUrl: './document-type-master.component.html',
  styleUrl: './document-type-master.component.scss',
})
export class DocumentTypeMasterComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('addModal') addModalRef!: ElementRef<HTMLElement>;
  @ViewChild('deleteModal') deleteModalRef!: ElementRef<HTMLElement>;
  @ViewChild('empPickerModal') empPickerModalRef!: ElementRef<HTMLElement>;
  @ViewChild('empPickerTable') empPickerTableRef?: VioTablesV2Component;

  records: any[] = [];
  employees: { _id: string; empNo: string; name: string; department?: string; role?: string }[] = [];
  isLoading = false;
  errorMessage = '';

  editingId: string | null = null;
  documentName = '';
  documentPeriod = '';
  documentFrequency = '';
  selectedEmpNos: string[] = [];
  isSaving = false;

  deletingId: string | null = null;
  deletingName = '';
  isDeleting = false;

  // Employee picker state
  tempSelectedEmpNos: string[] = [];

  get selectedEmployeeDetails() {
    return this.selectedEmpNos.map(no => this.employees.find(e => e.empNo === no)).filter(Boolean);
  }

  empPickerTableConfig: VT2_TABLE_CONFIG = {
    tableTitle: '',
    excelFileName: 'employees',
    columnLabels: {
      name: 'Name',
      empNo: 'Employee No.',
      department: 'Department',
      role: 'Role',
    },
    excludeFields: ['_id', 'createdAt', 'dob', 'esiNo', 'whatsappNumber', 'bu'],
    isSortable: true,
    enableRowSelection: true,
    showFormulaButton: false,
    showSummationButton: false,
    showHeaderGroupButton: false,
  };

  selectAllVisible(): void {
    this.empPickerTableRef?.selectAllPages();
  }

  clearAllVisible(): void {
    this.empPickerTableRef?.clearSelection();
  }

  readonly periodOptions = ['One time', 'Monthly', 'Yearly'];
  readonly typeOptions = ['Individual', 'Common', 'Conditional Access'];

  tableConfig: VT2_TABLE_CONFIG = {
    tableTitle: 'Document Type Master',
    excelFileName: 'document-type-master',
    columnLabels: {
      documentName: 'Document Name',
      documentPeriod: 'Document Period',
      documentFrequency: 'Document Frequency',
      createdAt: 'Added On',
    },
    excludeFields: ['_id'],
    dateFields: ['createdAt'],
    isSortable: true,
    showFormulaButton: false,
    showSummationButton: false,
    showHeaderGroupButton: false,
    actionButtons: [
      {
        icon: '✏️',
        label: 'Edit',
        title: 'Edit',
        onClick: (row) => this.openEditModal(row),
      },
      {
        icon: '🗑️',
        label: 'Delete',
        title: 'Delete',
        onClick: (row) => this.openDeleteModal(row),
      },
    ],
  };

  constructor(private apiService: ApiRequestsService) {}

  ngOnInit(): void {
    this.loadRecords();
    this.apiService.getApiCall('api/v1/employeemaster/list').subscribe({
      next: (res: any) => {
        if (res?.errorCode === this.apiService.NO_ERROR) this.employees = res.Data || [];
      },
    });
  }

  ngAfterViewInit(): void {
    this.prepareModal(this.addModalRef);
    this.prepareModal(this.deleteModalRef);
    this.prepareModal(this.empPickerModalRef);
  }

  ngOnDestroy(): void {
    this.detachModal(this.addModalRef);
    this.detachModal(this.deleteModalRef);
    this.detachModal(this.empPickerModalRef);
  }

  private prepareModal(modalRef: ElementRef<HTMLElement>): void {
    const modalEl = modalRef?.nativeElement;
    if (!modalEl) return;
    document.body.appendChild(modalEl);
    modalEl.addEventListener('hide.bs.modal', () => {
      if (modalEl.contains(document.activeElement)) {
        (document.activeElement as HTMLElement)?.blur();
      }
    });
  }

  private detachModal(modalRef: ElementRef<HTMLElement>): void {
    const modalEl = modalRef?.nativeElement;
    if (modalEl?.parentNode === document.body) {
      document.body.removeChild(modalEl);
    }
  }

  private showModal(modalRef: ElementRef<HTMLElement>): void {
    const modalEl = modalRef?.nativeElement;
    if (!modalEl) return;
    (bootstrap?.Modal?.getInstance(modalEl) || new bootstrap.Modal(modalEl)).show();
  }

  private hideModal(modalRef: ElementRef<HTMLElement>): void {
    const modalEl = modalRef?.nativeElement;
    if (!modalEl) return;
    (bootstrap?.Modal?.getInstance(modalEl) || new bootstrap.Modal(modalEl)).hide();
  }

  loadRecords() {
    this.isLoading = true;
    this.errorMessage = '';
    this.apiService.getApiCall('api/v1/documenttypemaster/list').subscribe({
      next: (res: any) => {
        this.isLoading = false;
        if (res && res.errorCode === this.apiService.NO_ERROR) {
          this.records = res.Data || [];
        } else {
          this.errorMessage = res?.errorDescription || 'Unable to load the list.';
        }
      },
      error: () => {
        this.isLoading = false;
        this.errorMessage = 'Unable to load the list. Please check your connection.';
      },
    });
  }

  openAddModal() {
    this.editingId = null;
    this.documentName = '';
    this.documentPeriod = '';
    this.documentFrequency = '';
    this.selectedEmpNos = [];
    this.errorMessage = '';
    this.showModal(this.addModalRef);
  }

  openEditModal(row: any) {
    this.editingId = row._id;
    this.documentName = row.documentName;
    this.documentPeriod = row.documentPeriod;
    this.documentFrequency = row.documentFrequency;
    this.selectedEmpNos = Array.isArray(row.authorizedEmpNos) ? [...row.authorizedEmpNos] : [];
    this.errorMessage = '';
    this.showModal(this.addModalRef);
  }

  onDocumentFrequencyChange(): void {
    if (this.documentFrequency !== 'Conditional Access') this.selectedEmpNos = [];
  }

  openEmpPicker(): void {
    this.tempSelectedEmpNos = [...this.selectedEmpNos];
    this.showModal(this.empPickerModalRef);
    setTimeout(() => {
      if (this.empPickerTableRef) {
        this.empPickerTableRef.selectedRows = new Set(
          this.employees.filter(e => this.selectedEmpNos.includes(e.empNo))
        );
      }
    }, 50);
  }

  onEmpSelectionChange(rows: any[]): void {
    this.tempSelectedEmpNos = rows.map(r => r.empNo);
  }

  confirmEmpSelection(): void {
    this.selectedEmpNos = [...this.tempSelectedEmpNos];
    this.hideModal(this.empPickerModalRef);
  }

  removeSelectedEmp(empNo: string): void {
    this.selectedEmpNos = this.selectedEmpNos.filter(e => e !== empNo);
  }


  openDeleteModal(row: any) {
    this.deletingId = row._id;
    this.deletingName = row.documentName;
    this.showModal(this.deleteModalRef);
  }

  async saveRecord() {
    if (!this.documentName.trim() || !this.documentPeriod || !this.documentFrequency) return;
    this.isSaving = true;
    this.errorMessage = '';
    const isEdit = !!this.editingId;
    const endpoint = isEdit ? 'api/v1/documenttypemaster/update' : 'api/v1/documenttypemaster/add';
    const payload: any = { documentName: this.documentName.trim(), documentPeriod: this.documentPeriod, documentFrequency: this.documentFrequency, authorizedEmpNos: this.selectedEmpNos };
    if (isEdit) payload['id'] = this.editingId;
    try {
      const res = await this.apiService.postApiCallReturningPromise(endpoint, payload);
      if (res && res.errorCode === this.apiService.NO_ERROR) {
        this.hideModal(this.addModalRef);
        this.loadRecords();
      } else {
        this.errorMessage = res?.errorDescription || `Unable to ${isEdit ? 'update' : 'add'} the record.`;
      }
    } catch {
      this.errorMessage = `Unable to ${isEdit ? 'update' : 'add'} the record.`;
    } finally {
      this.isSaving = false;
    }
  }

  async confirmDelete() {
    if (!this.deletingId) return;
    this.isDeleting = true;
    try {
      const res = await this.apiService.postApiCallReturningPromise('api/v1/documenttypemaster/delete', { id: this.deletingId });
      if (res && res.errorCode === this.apiService.NO_ERROR) {
        this.hideModal(this.deleteModalRef);
        this.loadRecords();
      } else {
        this.hideModal(this.deleteModalRef);
        this.errorMessage = res?.errorDescription || 'Unable to delete the record.';
      }
    } catch {
      this.hideModal(this.deleteModalRef);
      this.errorMessage = 'Unable to delete the record.';
    } finally {
      this.isDeleting = false;
      this.deletingId = null;
    }
  }
}
